import { type FC } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { FilterAltOff } from '@mui/icons-material';
import { Site } from '@/types/site.types';
import SiteFilterCard from './SiteFilterCard';

interface SiteFilterListProps {
    sites: Site[];
    loading: boolean;
    error: string | null;
    selectedSite: string | null;
    uploadingSiteId: string | null;
    isSuperAdmin: boolean;
    onSiteClick: (siteId: string) => void;
    onClearFilter: () => void;
    onAddPhotos: (siteName: string) => void;
}

/**
 * List of site filter cards with loading/error states and clear filter button
 */
const SiteFilterList: FC<SiteFilterListProps> = ({
    sites,
    loading,
    error,
    selectedSite,
    uploadingSiteId,
    isSuperAdmin,
    onSiteClick,
    onClearFilter,
    onAddPhotos
}) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <>
            {selectedSite && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterAltOff />}
                        onClick={onClearFilter}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Limpiar filtro
                    </Button>
                </Box>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                        lg: 'repeat(5, 1fr)',
                        xl: 'repeat(6, 1fr)'
                    },
                    gap: 2,
                    mx: { xs: 1, sm: 2 }
                }}
            >
                {sites.map((site, index) => {
                    const isSelected = selectedSite === site.id;
                    const isUploading = uploadingSiteId === site.Name;

                    return (
                        <SiteFilterCard
                            key={site.id || index}
                            site={site}
                            isSelected={isSelected}
                            isUploading={isUploading}
                            isSuperAdmin={isSuperAdmin}
                            onClick={() => onSiteClick(site.Name || site.id || '')}
                            onAddPhotos={onAddPhotos}
                        />
                    );
                })}
            </Box>
        </>
    );
};

export default SiteFilterList;
